import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';
import type { FormControlStatus, UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { map } from 'rxjs/operators';

import { UserSelectContainerComponent } from '@recruitee/account-ui';
import type { BillingInformation } from '@recruitee/billing-information-types';
import { emailValidator, phoneNumberValidatorFn } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  TaggerComponent,
  DividerComponent,
  FormErrorDirective,
  PhoneInputComponent,
  InputComponent,
  RtTooltip,
  IconHelpComponent,
  LabelComponent,
  FormFieldComponent,
} from '@recruitee/user-interface';

import type { BillingContactFormFields } from '../../types/pricing-feature.types';

@Component({
  selector: 'rt-billing-contact-form',
  templateUrl: './billing-contact-form.component.html',
  styleUrls: ['./billing-contact-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    IconHelpComponent,
    RtTooltip,
    UserSelectContainerComponent,
    InputComponent,
    PhoneInputComponent,
    FormErrorDirective,
    DividerComponent,
    TaggerComponent,
    TranslatePipe,
  ],
})
export class BillingContactFormComponent implements OnInit {
  public readonly phoneErrors: Record<string, string> = {
    invalidNumber: this.i18n.translate('app.common.errors.phone.invalid'),
    maxlength: this.i18n.translate('app.common.errors.phone.length'),
  };
  public form: UntypedFormGroup;
  public countryCode: string;

  @Input() public formValue: BillingInformation;
  @Input() public currentUserId: number;
  @Input() public fields: Array<keyof BillingContactFormFields> = [
    'fullName',
    'email',
    'phone',
    'invoiceEmails',
    'primaryContactId',
  ];

  public valueChanges = output<BillingContactFormFields>();
  public statusChanges = output<FormControlStatus>();

  constructor(
    private fb: UntypedFormBuilder,
    private i18n: I18nService,
  ) {}

  public ngOnInit(): void {
    this.form = this.fb.group({});

    if (this.fields.includes('fullName')) {
      this.form.addControl('fullName', this.fb.control(this.formValue.fullName));
    }

    if (this.fields.includes('email')) {
      this.form.addControl(
        'email',
        this.fb.control(this.formValue.email, [Validators.required, emailValidator]),
      );
    }

    if (this.fields.includes('phone')) {
      this.form.addControl('phone', this.fb.control(this.formValue.phone, phoneNumberValidatorFn));
    }

    if (this.fields.includes('primaryContactId')) {
      this.form.addControl(
        'primaryContactId',
        this.fb.control(this.currentUserId, [Validators.required]),
      );
    }

    if (this.fields.includes('invoiceEmails')) {
      this.form.addControl(
        'invoiceEmails',
        this.fb.control(this.mapToTaggerValue(this.formValue.invoiceEmails || [])),
      );
    }

    this.subscribeValueChanges();
    this.form.statusChanges.subscribe(status => this.statusChanges.emit(status));
  }

  private subscribeValueChanges(): void {
    this.form.valueChanges
      .pipe(
        map(formValue => {
          if (!this.fields.includes('invoiceEmails')) return formValue;

          return {
            ...formValue,
            invoiceEmails: this.mapFromTaggerValue(formValue.invoiceEmails),
          };
        }),
      )
      .subscribe(value => this.valueChanges.emit(value));
  }

  private mapFromTaggerValue(items: Array<{ isValid: boolean; email: string }>): string[] {
    return items.filter(({ isValid }) => isValid).map(({ email }) => email);
  }

  private mapToTaggerValue(emails: string[]): Array<{ isValid: boolean; email: string }> {
    return emails.map(email => ({ email, isValid: true }));
  }
}
